import { Search, Shield } from "lucide-react";

import { FieldNotesAdminTable } from "@/components/field-notes/FieldNotesAdminTable";
import { PunchNotesReviewList } from "@/components/field-notes/PunchNotesReviewList";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminFieldNotesViewModel } from "@/pages/dashboard/admin/useAdminFieldNotesViewModel";

export function AdminFieldNotesView() {
  const {
    canViewFieldNotesAdmin,
    canReviewFieldNotes,
    isDualManagerReadonly,
    isManagerOnly,
    isReadonlyAdmin,
    managedEditUserIds,
    fieldNotesAckFilter, setFieldNotesAckFilter,
    fieldNotesEmployeeFilter, setFieldNotesEmployeeFilter,
    fieldNotesEmployeeSearch, setFieldNotesEmployeeSearch,
    fieldNotesEmployeeOpen, setFieldNotesEmployeeOpen,
    fieldNotesEmployeeRef,
    fieldNotesEmployeeLabel, setFieldNotesEmployeeLabel,
    fieldNotesEmployeesData,
    fieldNotesReviewData, fieldNotesReviewLoading, fieldNotesReviewError, fieldNotesReviewErr, refetchFieldNotesReview,
    punchNotesReviewData, punchNotesReviewLoading, punchNotesReviewError, punchNotesReviewErr, refetchPunchNotesReview,
    unackedNotesTotal,
    fieldNoteProcessError,
    processFieldNoteMutation,
    ackPunchNotesReviewMutation,
  } = useAdminFieldNotesViewModel();
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
                {canViewFieldNotesAdmin && unackedNotesTotal > 0 && (
                  <Alert className="border-destructive/30 bg-destructive/10">
                    <Shield className="h-4 w-4 text-destructive" />
                    <AlertTitle className="text-destructive">Unacknowledged Field Notes</AlertTitle>
                    <div className="text-sm text-destructive">
                      There are <strong>{unackedNotesTotal}</strong> field note(s) awaiting acknowledgment.
                    </div>
                  </Alert>
                )}

                {canViewFieldNotesAdmin && (
                  <Card className="w-full max-w-full min-w-0 overflow-hidden">
                    <CardHeader className="min-w-0">
                      <CardTitle>Field Notes</CardTitle>
                      <CardDescription className="break-words [overflow-wrap:anywhere]">
                        {isDualManagerReadonly
                          ? "Account-wide view of standalone correction notes (Field Notes tab) and punch Clock-In/Out task/notes, injury, meals/break, and additional notes awaiting acknowledgment. You can acknowledge only notes for people on your managed teams (written to QuickBase). Out-of-team notes are view-only."
                          : isManagerOnly
                            ? "Standalone correction notes from your team (Field Notes tab) and punch Clock-In/Out task/notes, injury, meals/break, and additional notes awaiting acknowledgment. Unacknowledged notes can be acknowledged here (written to QuickBase)."
                            : isReadonlyAdmin
                              ? "Standalone correction notes for reporting (Field Notes tab submissions) and punch Clock-In/Out task/notes, injury, meals/break, and additional notes awaiting acknowledgment."
                              : "Standalone correction notes for the account (submitted from the Field Notes tab) and punch Clock-In/Out task/notes, injury, meals/break, and additional notes awaiting acknowledgment. Punch “Add note” on Recent Activity is different — see Admin Report → View notes on each punch."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 min-w-0 w-full max-w-full">
                      {fieldNoteProcessError && (
                        <Alert className="border-destructive/30 bg-destructive/10">
                          <AlertTitle className="text-destructive">Could not acknowledge</AlertTitle>
                          <div className="text-sm text-destructive">{fieldNoteProcessError}</div>
                        </Alert>
                      )}
                      <div className="flex flex-wrap gap-3 min-w-0">
                        <Select
                          value={fieldNotesAckFilter}
                          onValueChange={(v) => setFieldNotesAckFilter(v as "unacknowledged" | "all")}
                        >
                          <SelectTrigger className="w-full min-w-0 sm:w-[200px]">
                            <SelectValue placeholder="Acknowledgment" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unacknowledged">Unacknowledged</SelectItem>
                            <SelectItem value="all">All notes</SelectItem>
                          </SelectContent>
                        </Select>
                        {/* Searchable employee filter (full name or email) */}
                        <div className="relative w-full min-w-0 flex-1 sm:min-w-[240px] max-w-full sm:max-w-sm" ref={fieldNotesEmployeeRef}>
                          <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
                          <Input
                            className="pl-9 w-full min-w-0"
                            value={
                              fieldNotesEmployeeOpen
                                ? fieldNotesEmployeeSearch
                                : fieldNotesEmployeeFilter === "all"
                                  ? ""
                                  : fieldNotesEmployeeLabel
                            }
                            onChange={(e) => {
                              setFieldNotesEmployeeSearch(e.target.value);
                              if (!fieldNotesEmployeeOpen) setFieldNotesEmployeeOpen(true);
                            }}
                            onFocus={() => {
                              setFieldNotesEmployeeSearch("");
                              setFieldNotesEmployeeOpen(true);
                            }}
                            placeholder={
                              fieldNotesEmployeeOpen
                                ? "Search by name or email..."
                                : "All employees"
                            }
                          />
                          {fieldNotesEmployeeOpen && (
                            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                              <button
                                type="button"
                                className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                                onClick={() => {
                                  setFieldNotesEmployeeFilter("all");
                                  setFieldNotesEmployeeLabel("");
                                  setFieldNotesEmployeeOpen(false);
                                  setFieldNotesEmployeeSearch("");
                                }}
                              >
                                All employees
                              </button>
                              {(fieldNotesEmployeesData ?? []).map((u) => (
                                <button
                                  type="button"
                                  key={u.id}
                                  className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                                  onClick={() => {
                                    setFieldNotesEmployeeFilter(String(u.id));
                                    setFieldNotesEmployeeLabel(
                                      u.email ? `${u.full_name} (${u.email})` : u.full_name
                                    );
                                    setFieldNotesEmployeeOpen(false);
                                    setFieldNotesEmployeeSearch("");
                                  }}
                                >
                                  <div className="font-medium">{u.full_name}</div>
                                  <div className="text-xs text-muted-foreground">{u.email}</div>
                                </button>
                              ))}
                              {(fieldNotesEmployeesData ?? []).length === 0 && (
                                <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {(fieldNotesReviewLoading || punchNotesReviewLoading) && (
                        <div className="text-sm text-muted-foreground">Loading field notes…</div>
                      )}
                      {fieldNotesReviewError && (
                        <Alert className="border-destructive/30 bg-destructive/10">
                          <AlertTitle className="text-destructive">Could not load field notes</AlertTitle>
                          <div className="text-sm text-destructive">
                            {fieldNotesReviewErr instanceof Error
                              ? fieldNotesReviewErr.message
                              : "Request failed. Check that you are online and role is admin/manager/readonly_admin."}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => void refetchFieldNotesReview()}
                          >
                            Retry
                          </Button>
                        </Alert>
                      )}
                      {punchNotesReviewError && (
                        <Alert className="border-destructive/30 bg-destructive/10">
                          <AlertTitle className="text-destructive">Could not load punch notes</AlertTitle>
                          <div className="text-sm text-destructive">
                            {punchNotesReviewErr instanceof Error
                              ? punchNotesReviewErr.message
                              : "Request failed. Check that you are online and role is admin/manager/readonly_admin."}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => void refetchPunchNotesReview()}
                          >
                            Retry
                          </Button>
                        </Alert>
                      )}
                      {!fieldNotesReviewLoading && !punchNotesReviewLoading && !fieldNotesReviewError && !punchNotesReviewError && (
                        <>
                          <PunchNotesReviewList
                            rows={punchNotesReviewData ?? []}
                            onAck={canReviewFieldNotes ? (id) => ackPunchNotesReviewMutation.mutate(id) : undefined}
                            isProcessing={ackPunchNotesReviewMutation.isPending}
                            canAcknowledge={canReviewFieldNotes}
                            acknowledgeableUserIds={
                              isDualManagerReadonly
                                ? managedEditUserIds ?? new Set<number>()
                                : null
                            }
                          />
                          <FieldNotesAdminTable
                            notes={fieldNotesReviewData ?? []}
                            onProcess={canReviewFieldNotes ? (id) => processFieldNoteMutation.mutate(id) : undefined}
                            isProcessing={processFieldNoteMutation.isPending}
                            canAcknowledge={canReviewFieldNotes}
                            acknowledgeableUserIds={
                              isDualManagerReadonly
                                ? managedEditUserIds ?? new Set<number>()
                                : null
                            }
                            showAll={fieldNotesAckFilter === "all"}
                            hideEmpty={(punchNotesReviewData ?? []).length > 0}
                          />
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
    </div>
  );
}
